import React from 'react';
import { useApi } from '../hooks/useApi';
import { api } from '../api';
import { fmt, fmtDate } from '../utils';
import {
  Card, CardHeader, StatCard, StatGrid, Tag, Badge, SearchInput,
  Skeleton, PageWrap, PageHeader, Table,
} from '../components/UI';
import '../styles/Compliance.css';

/* ═══════════════════════════════════════════════════════════════════════════
   Unbooked Payments
═══════════════════════════════════════════════════════════════════════════ */
export function UnbookedPayments({ sourceId, syncRunId }) {
  const { data, loading } = useApi(() => api.unbookedPayments(sourceId, syncRunId), [sourceId, syncRunId]);
  const [search, setSearch] = React.useState('');

  const payments = React.useMemo(() => {
    let r = data?.payments || [];
    if (search) {
      const q = search.toLowerCase();
      r = r.filter(x =>
        x.party?.toLowerCase().includes(q) ||
        x.narration?.toLowerCase().includes(q) ||
        x.voucher_number?.toLowerCase().includes(q)
      );
    }
    return r;
  }, [data, search]);

  const total     = data?.totalAmount    ?? 0;
  const above90   = data?.above90Days    ?? 0;
  const highValue = data?.highValueCount ?? 0;
  const count     = data?.count          ?? 0;
  const overpaid  = data?.overpaidCount  ?? 0;

  const getAgeBucket = (days) => {
    const d = parseInt(days) || 0;
    if (d <= 30) return { label: '0–30d',  variant: 'green' };
    if (d <= 60) return { label: '31–60d', variant: 'amber' };
    if (d <= 90) return { label: '61–90d', variant: 'amber' };
    return            { label: '90d+',   variant: 'red'   };
  };

  const getAmountFlag = (amount) => {
    const a = parseFloat(amount) || 0;
    if (a >= 500000) return { label: '≥5L', variant: 'red'   };
    if (a >= 100000) return { label: '≥1L', variant: 'amber' };
    return null;
  };

  const getPartyFlag = (balance, group) => {
    const b = parseFloat(balance || 0);
    if (b < -0.01) return { label: 'Overpaid',   variant: 'red'   };
    if (b <  0.01) return { label: 'Settled',    variant: 'green' };
    if (group?.toLowerCase().includes('plant') ||
        group?.toLowerCase().includes('machinery'))
                   return { label: 'Capex',      variant: 'blue'  };
    return              { label: 'No Invoice',  variant: 'amber' };
  };

  return (
    <PageWrap>
      <PageHeader
        title="Unbooked Payments"
        sub="Payments made without a corresponding purchase invoice in Tally"
      />

      <StatGrid cols={5}>
        <StatCard label="Total Payments"  value={count}              accent={count     > 0 ? 'red'   : 'green'} />
        <StatCard label="Total Amount"    value={`₹${fmt(total)}`}   accent={total     > 0 ? 'red'   : 'green'} />
        <StatCard label="Over 90 Days"    value={above90}            accent={above90   > 0 ? 'red'   : 'green'} />
        <StatCard label="High Value (≥1L)"value={highValue}          accent={highValue > 0 ? 'amber' : 'green'} />
        <StatCard label="Overpaid"        value={overpaid}           accent={overpaid  > 0 ? 'red'   : 'green'} />
      </StatGrid>

      {overpaid > 0 && (
        <div className="co-alert co-alert--red" style={{ marginBottom: 12 }}>
          <span className="co-alert-icon">✗</span>
          <div className="co-alert-text">
            <strong>{overpaid} party/parties have a negative closing balance</strong> —
            payments exceed any billed amount. Verify whether a refund is due or
            invoices are missing from Tally.
          </div>
        </div>
      )}

      {count > 0 && (
        <div className="co-alert co-alert--amber" style={{ marginBottom: 16 }}>
          <span className="co-alert-icon">⚠</span>
          <div className="co-alert-text">
            <strong>{count} payment{count > 1 ? 's' : ''} totalling ₹{fmt(total)}</strong> have
            no corresponding purchase invoice in Tally. Bills may have been entered as journal
            vouchers instead of purchase invoices, or invoices are missing entirely.
          </div>
        </div>
      )}

      <Card>
        <CardHeader
          title={`${payments.length} Unbooked Payment${payments.length !== 1 ? 's' : ''}`}
          right={
            <SearchInput
              value={search}
              onChange={setSearch}
              placeholder="Search party or narration..."
            />
          }
        />
        {loading ? <Skeleton rows={5} /> : (
          <Table
            columns={[
              { label: '#', key: '_idx',
                render: r => <span className="co-dim">{r._idx}</span> },
              { label: 'Date', key: 'date', noWrap: true,
                render: r => (
                  <div>
                    <div className="co-date">{fmtDate(r.date)}</div>
                    <div className="co-date-sub">{parseInt(r.days_since_payment) || 0}d ago</div>
                  </div>
                )},
              { label: 'Voucher', key: 'voucher_number',
                render: r => <Tag>{r.voucher_number ?? r.voucherNumber}</Tag> },
              { label: 'Party', key: 'party',
                render: r => {
                  const flag = getPartyFlag(r.party_balance, r.party_group);
                  return (
                    <div>
                      <div className="co-party-name">{r.party || '—'}</div>
                      <div className="co-badge-wrap"><Badge text={flag.label} variant={flag.variant} /></div>
                    </div>
                  );
                }},
              { label: 'Group', key: 'party_group',
                render: r => r.party_group
                  ? <span className="co-dim-sm">{r.party_group}</span>
                  : <span className="co-dim">—</span> },
              { label: 'Payment Account', key: 'payment_account',
                render: r => r.payment_account
                  ? <Tag>{r.payment_account}</Tag>
                  : <span className="co-dim">—</span> },
              { label: 'Narration', key: 'narration', maxWidth: 200,
                render: r => (
                  <span className="co-narration">{r.narration || '—'}</span>
                )},
              { label: 'Amount (₹)', key: 'amount', right: true, mono: true,
                render: r => {
                  const flag = getAmountFlag(r.amount);
                  return (
                    <>
                      <span className="co-amount-red">₹{fmt(r.amount)}</span>
                      {flag && (
                        <div className="co-badge-wrap-sm">
                          <Badge text={flag.label} variant={flag.variant} />
                        </div>
                      )}
                    </>
                  );
                }},
              { label: 'Balance', key: 'party_balance', right: true, mono: true,
                render: r => {
                  const b = parseFloat(r.party_balance || 0);
                  const color = b < -0.01 ? 'var(--red)'
                              : b >  0.01 ? 'var(--amber)'
                              : 'var(--green)';
                  return (
                    <span className="co-balance" style={{ color }}>
                      {b < 0 ? '-' : ''}₹{fmt(Math.abs(b))}
                    </span>
                  );
                }},
              { label: 'Age', key: 'days_since_payment',
                render: r => {
                  const { label, variant } = getAgeBucket(r.days_since_payment);
                  return <Badge text={label} variant={variant} />;
                }},
            ]}
            rows={payments.map((r, i) => ({ ...r, _idx: i + 1 }))}
            emptyMsg="No unbooked payments found"
          />
        )}
      </Card>
    </PageWrap>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   Balance Check
═══════════════════════════════════════════════════════════════════════════ */
export function BalanceCheck({ sourceId, syncRunId }) {
  const { data, loading } = useApi(() => api.balanceCheck(sourceId, syncRunId), [sourceId, syncRunId]);
  const entries = data?.unbalancedEntries ?? data?.unbalanced_entries ?? [];
  const count   = data?.unbalancedCount   ?? data?.unbalanced_count  ?? entries.length;
  const ok      = data?.isFullyBalanced   ?? (count === 0);

  return (
    <PageWrap>
      <PageHeader
        title="Balance Sheet Check"
        sub="Vouchers where debits do not equal credits"
      />

      <StatGrid cols={2}>
        <StatCard
          label="Status"
          value={data == null ? '—' : ok ? 'Balanced' : 'Issues Found'}
          accent={ok ? 'green' : 'red'}
        />
        <StatCard
          label="Unbalanced Vouchers"
          value={count}
          accent={count > 0 ? 'red' : 'green'}
        />
      </StatGrid>

      {data != null && (
        <div
          className={`co-alert co-alert--${ok ? 'green' : 'red'}`}
          style={{ marginBottom: 16 }}
        >
          <span className="co-alert-icon">{ok ? '✓' : '✗'}</span>
          <div className="co-alert-text">
            {ok ? (
              <><strong>All vouchers are balanced.</strong> Every transaction has equal debits and credits.</>
            ) : (
              <><strong>{count} unbalanced voucher{count > 1 ? 's' : ''} found.</strong> These
                entries have mismatched debits and credits and must be corrected in Tally
                to ensure accurate financial statements.</>
            )}
          </div>
        </div>
      )}

      <Card>
        <CardHeader title="Unbalanced Entries" />
        {loading ? <Skeleton rows={4} /> : (
          <Table
            columns={[
              { label: '#', key: '_idx',
                render: r => <span className="co-dim">{r._idx}</span> },
              { label: 'Date', key: 'date', noWrap: true,
                render: r => (
                  <span className="co-date">{fmtDate(r.date)}</span>
                )},
              { label: 'Voucher No.', key: 'voucher_number',
                render: r => <Tag>{r.voucher_number ?? r.voucherNumber}</Tag> },
              { label: 'Type', key: 'voucher_type',
                render: r => <Tag>{r.voucher_type ?? r.voucherType}</Tag> },
              { label: 'Party', key: 'party',
                render: r => (
                  <span className="co-party-name">{r.party ?? r.raw_party ?? '—'}</span>
                )},
              { label: 'Discrepancy (₹)', key: 'discrepancy', right: true, mono: true,
                render: r => {
                  const v = parseFloat(r.discrepancy ?? r.entry_sum ?? 0);
                  return v ? (
                    <span className="co-amount-red">₹{fmt(Math.abs(v))}</span>
                  ) : (
                    <span className="co-dim">—</span>
                  );
                }},
            ]}
            rows={entries.map((r, i) => ({ ...r, _idx: i + 1 }))}
            emptyMsg="All vouchers are balanced"
          />
        )}
      </Card>
    </PageWrap>
  );
}
